import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { amendmentDrawerSections } from '@common/constants';
import {
  useCreateAmendment,
  useCreateCancellationAmendment,
  useGetAmendmentSubtypes,
  useGetClosedMonth,
  useGetSystemReferenceDate
} from '@common/slices';
import { AmendmentForm, AmendmentLookups } from '@common/types';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { useDynamicFormDrawerStyles } from '@components/dynamic-form/style';
import Loader from '@components/loader/Loader';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { useAppSelector } from '@core/redux/hooks';
import { RootState } from '@core/redux/store';
import {
  AmendmentIssueDateRuleResult,
  asClosedMonthRows,
  ClosedMonthRow,
  dateToFormValue,
  getAmendmentEffectiveDateBounds,
  getAmendmentIssueDateRules,
  isWithinIssueDateLov
} from '@features/production/amendment/utils';
import { filterAmendmentSubTypesByLineCode } from '@features/production/amendment/utils/filterAmendmentSubTypesByLineCode';
import { AmendmentCertificatesPopupAtom } from '@features/production/amendment/atoms';
import { DEFAULT_ERROR_TEXT } from '@utils';

import { useFormik } from 'formik';
import * as Yup from 'yup';

type Props = {
  policyId: string;
  policyLineCode?: string | number;
  policyType?: string;
  policyEffectiveDate?: string;
  policyExpiryDate?: string;
  policyIssueDate?: string;
  currencySymbol?: string;
  onSuccess?: (id?: string) => void;
};

type AmendmentDateContext = {
  systemReferenceDate: Date;
  closedMonthRows: readonly ClosedMonthRow[];
  issueRules: AmendmentIssueDateRuleResult;
  amendmentIssueDateDefaultStr: string;
};

type AmendmentDrawerFormProps = Props & {
  dateContext: AmendmentDateContext;
};

const AmendmentDrawerForm: React.FC<AmendmentDrawerFormProps> = ({
  policyId,
  policyLineCode,
  policyType,
  policyEffectiveDate,
  policyExpiryDate,
  currencySymbol = '',
  onSuccess,
  dateContext
}) => {
  const { classes } = useDynamicFormDrawerStyles();
  const { systemReferenceDate, issueRules, amendmentIssueDateDefaultStr } =
    dateContext;

  const buttonState = useSelector((s: RootState) => s.apiState.buttonState);
  const toMidnightUtcIso = (value: string | Date): string => {
    const d = new Date(value);
    const utcMidnight = new Date(
      Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
    );

    return utcMidnight.toISOString();
  };

  const defaultEffective = policyEffectiveDate;

  const effectiveDateBounds = useMemo(
    () =>
      getAmendmentEffectiveDateBounds(policyEffectiveDate, policyExpiryDate),
    [policyEffectiveDate, policyExpiryDate]
  );

  const allowAmendmentIssueDateEdit = !!useAppSelector(
    (s) => s.user?.info?.businessUser_AmendmentIssueDateEditRights
  );

  const { data: subtypesResp } = useGetAmendmentSubtypes();
  const [lookups, setLookups] = useState<AmendmentLookups | null>(null);

  const [createAmendment, { loading: amendmentSaving }] = useCreateAmendment();

  const [createCancellationAmendment, { loading: cancellationSaving }] =
    useCreateCancellationAmendment();

  const saving = amendmentSaving || cancellationSaving;

  const [certificatesPopupOpen, setCertificatesPopupOpen] = useState(false);
  const [pendingSubmitValues, setPendingSubmitValues] =
    useState<AmendmentForm | null>(null);

  const navigate = useNavigate();

  const allowPolicyEndorsement = !!useAppSelector(
    (s) => s.user?.info?.businessUser_AllowEndorsement
  );
  const allowPolicyCancellation = !!useAppSelector(
    (s) => s.user?.info?.businessUser_AllowPolicyCancellation
  );

  const initialValues = useMemo(
    () => ({
      amendmentType: '' as AmendmentForm['amendmentType'],
      amendmentSubtypeId: '',
      amendmentIssueDate: amendmentIssueDateDefaultStr,
      amendmentEffectiveDate: defaultEffective ?? '',
      amendmentReason: ''
    }),
    [amendmentIssueDateDefaultStr, defaultEffective]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        amendmentType: Yup.mixed<'POLICY_CANCELLATION' | 'POLICY_ENDORSEMENT'>()
          .oneOf(['POLICY_CANCELLATION', 'POLICY_ENDORSEMENT'])
          .required('Required'),
        amendmentSubtypeId: Yup.string().when('amendmentType', {
          is: 'POLICY_ENDORSEMENT',
          then: (schema) => schema.required('Required'),
          otherwise: (schema) => schema.notRequired()
        }),
        amendmentIssueDate: Yup.date()
          .required('Required')
          .test(
            'issue-range',
            'Date must be within the allowed range',
            (value) =>
              !!value &&
              isWithinIssueDateLov(
                value,
                issueRules.minDate,
                issueRules.maxDate
              )
          ),
        amendmentEffectiveDate: Yup.date()
          .required('Required')
          .when('amendmentType', {
            is: 'POLICY_CANCELLATION',
            then: (schema) =>
              effectiveDateBounds
                ? schema.test(
                    'effective-range',
                    'Date must be within the allowed range',
                    (value) =>
                      !!value &&
                      isWithinIssueDateLov(
                        value,
                        effectiveDateBounds.minDate,
                        effectiveDateBounds.maxDate
                      )
                  )
                : schema,
            otherwise: (schema) => schema
          }),
        amendmentReason: Yup.string().when('amendmentType', {
          is: 'POLICY_CANCELLATION',
          then: (schema) => schema.required('Required'),
          otherwise: (schema) => schema.notRequired()
        })
      }),
    [issueRules, effectiveDateBounds]
  );

  const getAmendmentSubtypeCode = useCallback(
    (subtypeId: string) =>
      (subtypesResp?.production?.getAmendmentSubTypes ?? []).find(
        (subtype) => subtype.id === subtypeId
      )?.code,
    [subtypesResp]
  );

  const requiresCertificatesPopup = useCallback(
    (values: AmendmentForm) => {
      const lineCode = String(policyLineCode ?? '');
      const subtypeCode = getAmendmentSubtypeCode(
        values.amendmentSubtypeId ?? ''
      );

      return (
        lineCode === '6' &&
        values.amendmentType === 'POLICY_ENDORSEMENT' &&
        (subtypeCode === 'EC10' || subtypeCode === 'EC11')
      );
    },
    [getAmendmentSubtypeCode, policyLineCode]
  );

  const pendingSubtypeCode = pendingSubmitValues
    ? getAmendmentSubtypeCode(pendingSubmitValues.amendmentSubtypeId ?? '')
    : undefined;
  const singleCertificateSelect = pendingSubtypeCode === 'EC11';

  const submitAmendment = useCallback(
    async (values: AmendmentForm, certificateIds?: string[]) => {
      const isCancellation = values.amendmentType === 'POLICY_CANCELLATION';
      const isEndorsement = values.amendmentType === 'POLICY_ENDORSEMENT';

      const variables = {
        input: {
          policyId,
          amendmentType: values.amendmentType,
          amendmentSubtype: isEndorsement
            ? (values.amendmentSubtypeId ?? '')
            : null,
          amendmentIssueDate: new Date(values.amendmentIssueDate).toISOString(),
          amendmentEffectiveDate: toMidnightUtcIso(values.amendmentEffectiveDate),
          amendmentReason: values.amendmentReason,
          ...(certificateIds !== undefined && { certificates: certificateIds })
        }
      };

      const handleSuccess = (newId: string) => {
        onSuccess?.(newId);
        if (newId) {
          navigate(`/production/amendments/${encodeURIComponent(newId)}`);
        }
      };

      if (isCancellation) {
        const { data } = await createCancellationAmendment(variables);
        const newId = data?.production?.createCancellationAmendment;
        handleSuccess(newId);
      } else {
        const { data } = await createAmendment(variables);
        const newId = data?.production?.createAmendment;
        handleSuccess(newId);
      }
    },
    [
      createAmendment,
      createCancellationAmendment,
      navigate,
      onSuccess,
      policyId,
      toMidnightUtcIso
    ]
  );

  const formik = useFormik<AmendmentForm>({
    initialValues,
    enableReinitialize: false,
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (requiresCertificatesPopup(values)) {
          setPendingSubmitValues(values);
          setCertificatesPopupOpen(true);

          return;
        }

        await submitAmendment(values);
      } catch (err) {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      }
    }
  });

  const handleCertificatesPopupClose = () => {
    setCertificatesPopupOpen(false);
    setPendingSubmitValues(null);
  };

  const handleCertificatesPopupSubmit = async (
    selectedCertificateIds: string[]
  ) => {
    if (!pendingSubmitValues) {
      return;
    }

    try {
      await submitAmendment(pendingSubmitValues, selectedCertificateIds);
      setCertificatesPopupOpen(false);
      setPendingSubmitValues(null);
    } catch (err) {
      toast.error(
        <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
      );
    }
  };

  useEffect(() => {
    const allAmendmentTypes: Record<string, string> = {
      POLICY_CANCELLATION: 'Policy Cancellation',
      POLICY_ENDORSEMENT: 'Policy Endorsement'
    };

    const amendmentTypes: Record<string, string> = {};

    if (allowPolicyCancellation) {
      amendmentTypes.POLICY_CANCELLATION =
        allAmendmentTypes.POLICY_CANCELLATION;
    }

    const lineCode = String(policyLineCode ?? '');
    let showPolicyEndorsement = false;

    if (allowPolicyEndorsement) {
      if (policyType === 'GROUP') {
        showPolicyEndorsement = lineCode === '6';
      } else if (policyType === 'CERTIFICATE') {
        showPolicyEndorsement = lineCode !== '6';
      } else if (policyType === 'INDIVIDUAL') {
        showPolicyEndorsement = lineCode !== '50';
      }
    }

    if (showPolicyEndorsement) {
      amendmentTypes.POLICY_ENDORSEMENT = allAmendmentTypes.POLICY_ENDORSEMENT;
    }

    const amendmentSubtypes: Record<string, string> =
      filterAmendmentSubTypesByLineCode(
        subtypesResp?.production?.getAmendmentSubTypes ?? [],
        lineCode
      ).reduce<Record<string, string>>((acc, it) => {
        acc[it.id] = it.title;
        return acc;
      }, {});

    const allowedKeys = Object.keys(amendmentTypes);
    if (
      formik.values.amendmentType &&
      !allowedKeys.includes(formik.values.amendmentType)
    ) {
      formik.setFieldValue('amendmentType', '');
      formik.setFieldValue('amendmentSubtypeId', '');
    }

    setLookups({
      amendmentTypes,
      amendmentSubtypes,
      policyEffectiveDate,
      policyExpiryDate,
      amendmentIssueDateMinDate: issueRules.minDate,
      amendmentIssueDateMaxDate: issueRules.maxDate,
      amendmentIssueDateEditable: allowAmendmentIssueDateEdit
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    subtypesResp,
    policyEffectiveDate,
    policyExpiryDate,
    allowPolicyCancellation,
    allowPolicyEndorsement,
    policyLineCode,
    policyType,
    issueRules,
    allowAmendmentIssueDateEdit
  ]);

  const handleChange = useCallback(
    (name: string, value: string | number | boolean | Date) => {
      if (name === 'amendmentIssueDate' || name === 'amendmentEffectiveDate') {
        const dateValue =
          value instanceof Date ? dateToFormValue(value) : String(value);
        formik.setFieldValue(name, dateValue);
        formik.setFieldTouched(name, true);

        return;
      }
      formik.setFieldValue(name, value);
      formik.setFieldTouched(name, true);
      if (name === 'amendmentType' && value !== 'POLICY_ENDORSEMENT') {
        formik.setFieldValue(
          'amendmentIssueDate',
          amendmentIssueDateDefaultStr,
          false
        );
        formik.setFieldValue(
          'amendmentEffectiveDate',
          dateToFormValue(systemReferenceDate),
          false
        );
        formik.setFieldValue('amendmentSubtypeId', '');
      } else if (name === 'amendmentType' && value === 'POLICY_ENDORSEMENT') {
        formik.setFieldValue(
          'amendmentIssueDate',
          amendmentIssueDateDefaultStr,
          false
        );
        formik.setFieldValue(
          'amendmentEffectiveDate',
          defaultEffective ?? '',
          false
        );
      }
    },
    [
      formik,
      amendmentIssueDateDefaultStr,
      defaultEffective,
      systemReferenceDate
    ]
  );

  const sections = useMemo(
    () => (lookups ? amendmentDrawerSections(formik as any, lookups) : []),
    [lookups, formik.values]
  );

  const noPermittedAmendmentTypes =
    !lookups || Object.keys(lookups.amendmentTypes || {}).length === 0;

  if (!lookups) {
    return (
      <div className={classes.loaderContainer}>
        <Loader />
      </div>
    );
  }

  return (
    <div>
      {noPermittedAmendmentTypes && <div style={{ marginBottom: 12 }}></div>}
      <DynamicFormWidget
        onChange={handleChange}
        sections={sections}
        disableForm={saving || noPermittedAmendmentTypes}
        onSubmit={() => formik.handleSubmit()}
        submitButtonState={buttonState}
      />
      {certificatesPopupOpen && policyLineCode != null && (
        <AmendmentCertificatesPopupAtom
          open={certificatesPopupOpen}
          policyId={policyId}
          lineCode={String(policyLineCode)}
          currencySymbol={currencySymbol}
          onClose={handleCertificatesPopupClose}
          onSubmit={handleCertificatesPopupSubmit}
          submitting={saving}
          singleSelect={singleCertificateSelect}
        />
      )}
    </div>
  );
};

const AmendmentDrawer: React.FC<Props> = (props) => {
  const { classes } = useDynamicFormDrawerStyles();
  const { data: systemRefResp, loading: systemRefLoading } =
    useGetSystemReferenceDate();
  const { data: closedMonthResp, loading: closedMonthLoading } =
    useGetClosedMonth({ variables: { type: 'PRD' } });
  const todayRef = useRef(new Date());
  const [dateContext, setDateContext] = useState<AmendmentDateContext | null>(
    null
  );

  const isDateDataReady =
    !systemRefLoading &&
    !closedMonthLoading &&
    !!systemRefResp?.core?.getSystemReferenceDate;

  useEffect(() => {
    if (!isDateDataReady || dateContext) {
      return;
    }

    const systemReferenceDate = new Date(
      systemRefResp.core.getSystemReferenceDate
    );
    const closedMonthRows = asClosedMonthRows(
      closedMonthResp?.core?.GetClosedMonth
    );
    const issueRules = getAmendmentIssueDateRules(
      closedMonthRows,
      systemReferenceDate,
      todayRef.current
    );
    const amendmentIssueDateDefaultStr = dateToFormValue(issueRules.defaultDate);

    setDateContext({
      systemReferenceDate,
      closedMonthRows,
      issueRules,
      amendmentIssueDateDefaultStr
    });
  }, [isDateDataReady, systemRefResp, closedMonthResp, dateContext]);

  if (!dateContext) {
    return (
      <div className={classes.loaderContainer}>
        <Loader />
      </div>
    );
  }

  return <AmendmentDrawerForm {...props} dateContext={dateContext} />;
};

export { AmendmentDrawer };
